import { createContext, useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import { createEnhancedAblyClient, mockFetchAuthToken } from '../utils/ablyClientUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const AblyContext = /*#__PURE__*/createContext({});
export const AblyContextProvider = ({
  children,
  ablyChannelId,
  fetchAuthToken,
  clientOptions,
  subscriptionHandler,
  tokenDetails
}) => {
  const channelRef = useRef(null);
  const hasSubscribed = useRef(false);
  const ablyRealtimeClient = useMemo(() => {
    if (!ablyChannelId) {
      return null;
    }
    try {
      const options = Object.assign({}, clientOptions, {
        useTokenAuth: true
      });
      if (tokenDetails) {
        options.tokenDetails = tokenDetails;
      }
      options.authCallback = fetchAuthToken || mockFetchAuthToken; // the mock is added to remove console warnings

      return createEnhancedAblyClient(options);
    } catch (e) {
      Raven.captureMessage('Error creating Ably client', {
        extra: {
          component: 'AblyRealtimeClientProvider',
          error: e
        }
      });
      return null;
    }
  }, [ablyChannelId, clientOptions, fetchAuthToken, tokenDetails]);
  useEffect(() => {
    if (!ablyRealtimeClient || !ablyChannelId) {
      return;
    }

    // Only create a new channel and subscribe if we haven't already
    if (!channelRef.current || !hasSubscribed.current) {
      // Create the channel
      channelRef.current = ablyRealtimeClient.channels.get(ablyChannelId);

      // Enable channel rewind to get any missed messages
      try {
        channelRef.current.setOptions({
          params: {
            rewind: '10'
          }
        });
      } catch (e) {
        Raven.captureException(e, {
          extra: {
            channelId: ablyChannelId
          }
        });
      }

      // Ensure client is connected before subscribing
      const connectAndSubscribe = () => {
        try {
          channelRef.current.subscribe(subscriptionHandler);
          hasSubscribed.current = true;
        } catch (e) {
          Raven.captureException(e, {
            extra: {
              channelId: ablyChannelId
            }
          });
        }
      };

      // Check connection state and subscribe when ready
      if (ablyRealtimeClient.connection.state === 'connected') {
        connectAndSubscribe();
      } else {
        // Listen for connection state changes
        const onConnectionStateChange = stateChange => {
          if (stateChange.current === 'connected') {
            connectAndSubscribe();
            ablyRealtimeClient.connection.off(onConnectionStateChange);
          }
        };
        ablyRealtimeClient.connection.once('connected', onConnectionStateChange);

        // Force connection if not connecting already
        if (ablyRealtimeClient.connection.state === 'initialized' || ablyRealtimeClient.connection.state === 'disconnected') {
          ablyRealtimeClient.connection.connect();
        }
      }
    }

    // Cleanup function
    return () => {
      if (channelRef.current && hasSubscribed.current) {
        try {
          channelRef.current.unsubscribe(subscriptionHandler);
          channelRef.current.detach();
          hasSubscribed.current = false;
          channelRef.current = null;
        } catch (e) {
          Raven.captureException(e, {
            extra: {
              channelId: ablyChannelId
            }
          });
        }
      }
    };
  }, [ablyChannelId, ablyRealtimeClient, subscriptionHandler]);
  return /*#__PURE__*/_jsx(AblyContext.Provider, {
    value: {
      ablyRealtimeClient
    },
    children: children
  });
};